import { renderToStaticMarkup } from "react-dom/server";
import { Feature } from "geojson";
import * as L from "leaflet";
import { useEffect, useState } from "react";

import { removeAllDrawnLayers } from "src/utils/map.utils";
import { MapMarkerIcon } from "src/components/icons/MapMarkerIcon";

export type MarkerStatus = "default" | "active" | "new";

export function useDrawMarker(map: L.Map, status: MarkerStatus = "default") {
  const [marker, setMarker] = useState<Feature | null>(null);

  useEffect(() => {
    return function cleanup() {
      stopEditDrawing();
      removeAllDrawings();
    };
  }, []);

  function renderInitialMarker(initialMarker: Feature) {
    const layer = L.geoJSON(initialMarker, {
      pointToLayer: (geoJsonPoint, latlng) => getLeafletMarker(latlng, status),
    });
    layer.addTo(map);
    layer.on("pm:edit", (event) => {
      // @ts-ignore: incomplete type
      setMarker(event.layer.toGeoJSON());
    });
    setMarker(initialMarker);
  }

  function stopDrawing() {
    map.pm.disableDraw("Marker");
  }

  function startDrawing() {
    map.pm.enableDraw("Marker", {
      markerStyle: { icon: getMarkerIcon(status) },
    });

    map.on("pm:create", (event) => {
      const { layer } = event;
      // @ts-ignore: incomplete type
      setMarker(layer.toGeoJSON());
      map.pm.disableDraw("Marker");

      layer.on("pm:edit", (event: L.LeafletEvent) => {
        const { layer } = event;
        setMarker(layer.toGeoJSON());
      });
    });

    return function cleanup() {
      map.off("pm:create");
    };
  }

  function redrawDrawing() {
    setMarker(null);
    removeAllDrawings();

    setTimeout(() => {
      startDrawing();
    }, 100);
  }

  function editDrawing() {
    map.pm.enableGlobalEditMode({});
  }

  function stopEditDrawing() {
    map.pm.disableGlobalEditMode();
  }

  function removeAllDrawings() {
    removeAllDrawnLayers(map);
  }

  return {
    marker,
    renderInitialMarker,
    startDrawing,
    stopDrawing,
    editDrawing,
    redrawDrawing,
    stopEditDrawing,
    removeAllDrawings,
  };
}

export function getLeafletMarker(latlng: L.LatLngLiteral, status: MarkerStatus, options?: L.MarkerOptions | undefined) {
  const icon = getMarkerIcon(status);
  const marker = L.marker(latlng, {
    icon,
    ...options,
  });
  return marker;
}

export function getMarkerIcon(status: MarkerStatus) {
  function defineColor(status: string) {
    switch (status) {
      case "default":
        return "#3fabff";
      case "active":
        return "#E2646B";
      case "new":
        return "grey";
    }
  }
  return new L.DivIcon({
    html: renderToStaticMarkup(<MapMarkerIcon color={defineColor(status)} />),
    iconSize: [33, 44],
    iconAnchor: [33 / 2, 44],
  });
}
